/**
 * lightningConfig — the pure math for the `lightning` atmosphere kind. A strike
 * anchor fires every `intervalMs` (jittered per anchor): mostly dark, with a sharp
 * DOUBLE-FLASH envelope inside a short strike window (fast attack → dim → brighter
 * peak → decay), the classic lightning read. Kept pure + TDD'd; the sprite apply-
 * loop lives in lightningEffect.ts. Reduce-motion gating is the effect's concern
 * (this just describes the waveform).
 */
import { seededRand } from '../atmosphere/atmospherePlacement';

/** Length of a single strike's visible flash window (ms). */
export const STRIKE_WINDOW_MS = 220;

/**
 * Resolve a stable per-anchor interval in `[minMs, maxMs]` from a seed — so each
 * bolt fires on its own cadence and they never strobe in lockstep.
 */
export function strikeIntervalMs(seed: number, minMs: number, maxMs: number): number {
  const lo = Math.max(1, Math.min(minMs, maxMs));
  const hi = Math.max(lo, Math.max(minMs, maxMs));
  return lo + (hi - lo) * seededRand(seed);
}

/**
 * The 0..1 flash envelope for an anchor at `elapsedMs`. The anchor strikes once
 * per `intervalMs`; within the `STRIKE_WINDOW_MS` after each strike the value
 * traces a double-flash (rise → partial dim → brighter peak → decay), and is 0
 * the rest of the time. `seed` offsets the FIRST strike so anchors desync.
 */
export function strikeAlpha(elapsedMs: number, intervalMs: number, seed = 0): number {
  const period = Math.max(STRIKE_WINDOW_MS, intervalMs);
  // Desync: push each anchor's first strike out by up to one interval.
  const offset = seededRand(seed + 7) * period;
  const since = (((elapsedMs - offset) % period) + period) % period; // 0..period
  if (since >= STRIKE_WINDOW_MS) return 0; // dark between strikes
  const u = since / STRIKE_WINDOW_MS; // 0..1 across the flash window
  // Piecewise double-flash: lead flash, brief dim, brighter main flash, decay.
  if (u < 0.12) return (u / 0.12) * 0.6; // fast attack to the lead flash
  if (u < 0.24) return 0.6 - ((u - 0.12) / 0.12) * 0.4; // dim to 0.2
  if (u < 0.4) return 0.2 + ((u - 0.24) / 0.16) * 0.8; // surge to the 1.0 peak
  return Math.max(0, 1 - (u - 0.4) / 0.6); // long decay to 0
}
